import React, { useState } from 'react';
import './RegistrationPage.css';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import API_BASE_URL from '../config';

const RegistrationPage = () => {
  const [formData, setFormData] = useState({
    name: '',  // changed from fullName
    email: '',
    password: '',
    userType: '',
    termsAccepted: false,
  });
  const [step, setStep] = useState('register'); // 'register' | 'verify'
  const [otp, setOtp] = useState('');
  const [status, setStatus] = useState('');
  
  const nav = useNavigate();

  const handleGoogleSuccess = async (credentialResponse) => {
    const idToken = credentialResponse?.credential;
    if (!idToken) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        alert(data.message || data.error || 'Google sign-in failed');
        return;
      }
      const token = data.token || data.accessToken;
      if (token) {
        localStorage.setItem('token', token);
        if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
        nav('/home', { replace: true });
      } else {
        alert('No token received from server.');
      }
    } catch (err) {
      console.error(err);
      alert('Google sign-in failed. Please try again.');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.termsAccepted) {
      alert('Please accept the terms and conditions.');
      return;
    }

    try {
      const registerUrl = `${API_BASE_URL}/api/register`;
      console.log('📝 Attempting registration to:', registerUrl);
      console.log('📦 Registration data:', { ...formData, password: '***' });
      console.log('🌐 API Base URL:', API_BASE_URL);
      
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const res = await fetch(registerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      console.log('📡 Response status:', res.status, res.statusText);
      console.log('📡 Response headers:', Object.fromEntries(res.headers.entries()));

      // Try to parse JSON response
      let data;
      try {
        const responseText = await res.text();
        console.log('📄 Raw response:', responseText);
        
        if (!responseText) {
          throw new Error('Empty response from server');
        }
        
        data = JSON.parse(responseText);
        console.log('✅ Parsed response:', data);
      } catch (parseError) {
        console.error('❌ Failed to parse response as JSON:', parseError);
        const text = await res.text().catch(() => 'Unable to read response');
        console.error('📄 Response text:', text);
        alert(`❌ Registration failed: Server returned invalid response (Status: ${res.status})\n\nResponse: ${text.substring(0, 200)}`);
        return;
      }

      if (res.ok) {
        console.log('✅ User saved:', data);
        setStatus('Registration successful. Please check your email for the verification code.');
        setStep('verify');
      } else {
        // Show detailed error from server
        const errorMsg = data.error || data.message || 'Unknown error';
        const errorDetails = data.details || '';
        const errorCode = data.code || '';
        
        console.error('❌ Registration failed:', { 
          status: res.status, 
          error: errorMsg, 
          details: errorDetails,
          code: errorCode,
          fullData: data 
        });
        
        let alertMessage = `❌ Registration failed: ${errorMsg}`;
        if (errorDetails) {
          alertMessage += `\n\nDetails: ${errorDetails}`;
        }
        if (errorCode) {
          alertMessage += `\n\nError Code: ${errorCode}`;
        }
        if (data.help) {
          alertMessage += `\n\nHelp: ${data.help}`;
        }
        
        alert(alertMessage);
      }
    } catch (err) {
      console.error('❌ Registration error:', err);
      console.error('Error name:', err.name);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      
      // Provide more specific error messages
      let errorMessage = 'Registration failed due to network/server error.';
      let errorDetails = '';
      
      if (err.name === 'AbortError') {
        errorMessage = 'Request timeout - server took too long to respond.';
        errorDetails = `The backend server at ${API_BASE_URL} did not respond within 30 seconds.\n\nPlease check:\n1. Backend server is running\n2. Server is not overloaded\n3. Network connection is stable`;
      } else if (err.name === 'TypeError' && err.message.includes('Failed to fetch')) {
        errorMessage = 'Cannot connect to backend server.';
        errorDetails = `Unable to reach: ${API_BASE_URL}\n\nPlease check:\n` +
          `1. Backend server is running\n` +
          `2. Network connection is active\n` +
          `3. CORS is properly configured\n` +
          `4. Backend URL is correct in config.js\n` +
          `5. Firewall is not blocking the connection`;
      } else if (err.message) {
        errorMessage = `Registration failed: ${err.message}`;
        errorDetails = 'Check browser console (F12) for more details.';
      }
      
      alert(`❌ ${errorMessage}\n\n${errorDetails}\n\nCheck browser console for details.`);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setStatus('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, otp }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus(data.error || 'Verification failed');
        return;
      }
      setStatus('Email verified successfully. You can now log in.');
      setTimeout(() => nav('/login'), 1200);
    } catch (err) {
      setStatus('Verification failed. Please try again.');
    }
  };

  const handleResend = async () => {
    setStatus('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });
      const data = await res.json().catch(() => ({}));
      setStatus(data.message || 'If this email is registered, a verification code has been sent.');
    } catch (err) {
      setStatus('Could not resend code. Please try again.');
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <h2>{step === 'register' ? 'Create Your Account' : 'Verify Your Email'}</h2>
        <p>{step === 'register' ? "Join Creator's Arch" : 'Enter the 6-digit code sent to your email.'}</p>

        {status && (
          <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#444' }}>
            {status}
          </p>
        )}

        {step === 'register' ? (
        <form onSubmit={handleSubmit}>
        <label>Full Name</label>
<input
  type="text"
  name="name" // changed from fullName
  required
  placeholder="John Doe"
  value={formData.name}
  onChange={handleChange}
/>


          <label>Email</label>
          <input
            type="email"
            name="email"
            required
            placeholder="you@example.com"
            value={formData.email}
            onChange={handleChange}
          />

          <label>Password</label>
          <input
            type="password"
            name="password"
            required
            placeholder="********"
            value={formData.password}
            onChange={handleChange}
          />

          <label>Select User Type</label>
<select
  name="userType"
  required
  value={formData.userType}
  onChange={handleChange}
>
  <option value="">-- Select User Type --</option>
  <option value="recruiter">Recruiter</option>
  <option value="talent">Talent</option>
</select>


          <div className="checkbox">
            <input
              type="checkbox"
              name="termsAccepted"
              checked={formData.termsAccepted}
              onChange={handleChange}
            />
            <label>I agree to the terms & conditions</label>
          </div>

          <button type="submit">Register</button>

          {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
            <>
              <p style={{ margin: '1rem 0 0.5rem', fontSize: '0.9rem', color: '#666' }}>— or —</p>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => alert('Google sign-in was cancelled or failed.')}
                  useOneTap={false}
                  theme="outline"
                  size="large"
                  text="signup_with"
                />
              </div>
            </>
          )}
        </form>
        ) : (
          <form onSubmit={handleVerify}>
            <label>Email</label>
            <input
              type="email"
              name="email"
              required
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
            />

            <label>Verification Code</label>
            <input
              type="text"
              name="otp"
              required
              placeholder="6-digit code"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />

            <button type="submit">Verify Email</button>

            <div className="login-footer" style={{ marginTop: '0.75rem' }}>
              <button
                type="button"
                className="link-button"
                onClick={handleResend}
              >
                Resend code
              </button>
              <span style={{ margin: '0 0.5rem' }}>|</span>
              <button
                type="button"
                className="link-button"
                onClick={() => nav('/login')}
              >
                Back to Login
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default RegistrationPage;
